import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, DatePicker, Skeleton, Tooltip, Typography } from "antd";
import {
  FireOutlined,
  TrophyOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { DualAxes } from "@ant-design/plots";
import moment from "moment";
import { Title } from "components/common";
import { getTimeDateString } from "utils/date";
import { useGetDashboardV2PerformanceChartQuery } from "../../api";
import { DASHBOARD_USE_MOCK_DATA } from "utils/featureFlags";
import { mockChartData } from "../../mockData";
import styles from "./DashboardStreakPanel.module.less";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Shared tooltip style — white card with border, replaces the default dark popup
const CHIP_TOOLTIP = {
  color: "#fff",
  placement: "top",
  overlayInnerStyle: {
    background: "#fff",
    color: "#334155",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 12.5,
    lineHeight: 1.55,
    boxShadow: "0 6px 24px rgba(0,0,0,0.10)",
    border: "1px solid #e6f0ff",
  },
  overlayStyle: { maxWidth: 230 },
};

const CHART_DATE_PRESETS = {
  "Last 7 Days":   [moment().subtract(6,  "days").startOf("day"), moment().endOf("day")],
  "Last 30 Days":  [moment().subtract(29, "days").startOf("day"), moment().endOf("day")],
  "Last 3 Months": [moment().subtract(3,  "months").startOf("day"), moment().endOf("day")],
  "Last 6 Months": [moment().subtract(6,  "months").startOf("day"), moment().endOf("day")],
};

// ── Series definitions ──────────────────────────────────────────────────────
// accuracy + threshold  → right Y-axis (0–100%)
// correct  + wrong      → left  Y-axis (question counts)
const SERIES = [
  { key: "accuracy",  color: "#4b93ff", label: "Accuracy %",           solid: true,  axis: "right" },
  { key: "correct",   color: "#16a34a", label: "Correct Answers",       solid: true,  axis: "left"  },
  { key: "wrong",     color: "#dc2626", label: "Wrong Answers",         solid: true,  axis: "left"  },
  { key: "threshold", color: "#f59e0b", label: "Pass Threshold (60%)",  solid: false, axis: "right" },
];
const ALL_KEYS = SERIES.map((s) => s.key);

// ── Trend chart with custom legend ─────────────────────────────────────────
const TrendChart = ({ data }) => {
  const [active, setActive] = useState(new Set(ALL_KEYS));

  const allOn = active.size === ALL_KEYS.length;

  const toggle = (key) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return prev; // keep at least one on
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const toggleAll = () =>
    setActive(allOn ? new Set(["accuracy"]) : new Set(ALL_KEYS));

  // ── Left-axis data: correct + wrong counts ────────────────────────────
  const countData = useMemo(() => {
    const rows = [];
    data.forEach((item) => {
      if (active.has("correct"))
        rows.push({ date: item.date, count: item.correct ?? 0, type: "Correct" });
      if (active.has("wrong"))
        rows.push({ date: item.date, count: item.incorrect ?? 0, type: "Wrong" });
    });
    return rows;
  }, [data, active]);

  // ── Right-axis data: accuracy % + optional flat threshold line ────────
  const pctData = useMemo(() => {
    const rows = [];
    data.forEach((item) => {
      if (active.has("accuracy"))
        rows.push({ date: item.date, pct: item.accuracy, series: "Accuracy %" });
      if (active.has("threshold"))
        rows.push({ date: item.date, pct: 60, series: "Pass Threshold" });
    });
    return rows;
  }, [data, active]);

  const chartConfig = {
    data: [countData, pctData],
    xField: "date",
    yField: ["count", "pct"],
    legend: false,
    smooth: true,
    animation: { appear: { animation: "wave-in", duration: 1000 } },
    geometryOptions: [
      // Left axis — correct & wrong counts (no persistent dots — tooltip shows on hover)
      {
        geometry: "line",
        seriesField: "type",
        smooth: true,
        color: ["#16a34a", "#dc2626"],
        lineStyle: { lineWidth: 2.5 },
        point: { size: 0 },
      },
      // Right axis — accuracy % + pass threshold
      {
        geometry: "line",
        seriesField: "series",
        smooth: true,
        color: ({ series }) =>
          series === "Pass Threshold" ? "#f59e0b" : "#4b93ff",
        lineStyle: ({ series }) =>
          series === "Pass Threshold"
            ? { lineDash: [5, 4], lineWidth: 1.5, opacity: 0.9 }
            : { lineWidth: 2.5 },
        point: { size: 0 },
      },
    ],
    yAxis: {
      count: {
        min: 0,
        tickCount: 5,
        title: { text: "Questions", style: { fontSize: 12, fill: "#94a3b8" } },
        grid: { line: { style: { stroke: "#f0f5ff", lineWidth: 1 } } },
        label: { style: { fill: "#94a3b8", fontSize: 11 } },
      },
      pct: {
        min: 0,
        max: 100,
        tickCount: 5,
        title: { text: "Accuracy %", style: { fontSize: 12, fill: "#94a3b8" } },
        label: {
          formatter: (v) => `${v}%`,
          style: { fill: "#94a3b8", fontSize: 11 },
        },
      },
    },
    xAxis: {
      tickCount: 6,
      label: { style: { fill: "#64748b", fontSize: 11 } },
    },
    tooltip: {
      shared: true,
      showCrosshairs: true,
      crosshairs: {
        type: "x",
        line: { style: { stroke: "#c4d8f5", lineWidth: 1 } },
      },
      customItems: (items) =>
        items.map((item) => ({
          ...item,
          value:
            item.name === "Accuracy %" || item.name === "Pass Threshold"
              ? `${item.value}%`
              : `${item.value} questions`,
        })),
    },
  };

  // ── Summary stats (shown above chart) ────────────────────────────────
  const summaryStats = useMemo(() => {
    if (!data.length) return null;
    const first   = data[0].accuracy;
    const latest  = data[data.length - 1].accuracy;
    const change  = parseFloat((latest - first).toFixed(1));
    const totalQ  = data.reduce((s, d) => s + (d.correct ?? 0) + (d.incorrect ?? 0), 0);
    const totalC  = data.reduce((s, d) => s + (d.correct ?? 0), 0);
    const bestAcc = Math.max(...data.map((d) => d.accuracy));
    return { latest, change, totalQ, totalC, bestAcc };
  }, [data]);

  return (
    <div>
      {/* ── Summary strip ── */}
      {summaryStats && (
        <div className={styles.chartSummary}>
          <Tooltip
            title={<span>Accuracy of your <strong>most recent</strong> quiz attempt in this period</span>}
            {...CHIP_TOOLTIP}
          >
            <div className={styles.summaryChip}>
              <span className={styles.summaryDot} style={{ background: "#4b93ff" }} />
              <span className={styles.summaryLabel}>Latest</span>
              <span className={styles.summaryVal}>{summaryStats.latest}%</span>
            </div>
          </Tooltip>

          <Tooltip
            title={
              <span>
                Net accuracy change from your <strong>first to last</strong> quiz in this period.{" "}
                {summaryStats.change >= 0 ? "You are improving!" : "Needs attention."}
              </span>
            }
            {...CHIP_TOOLTIP}
          >
            <div className={styles.summaryChip}>
              <span
                className={styles.summaryDot}
                style={{ background: summaryStats.change >= 0 ? "#16a34a" : "#dc2626" }}
              />
              <span className={styles.summaryLabel}>Since start</span>
              <span
                className={styles.summaryVal}
                style={{ color: summaryStats.change >= 0 ? "#16a34a" : "#dc2626" }}
              >
                {summaryStats.change >= 0 ? "+" : ""}{summaryStats.change}%
              </span>
            </div>
          </Tooltip>

          <Tooltip
            title={<span>Your <strong>highest single-quiz</strong> accuracy in this period</span>}
            {...CHIP_TOOLTIP}
          >
            <div className={styles.summaryChip}>
              <span className={styles.summaryDot} style={{ background: "#f59e0b" }} />
              <span className={styles.summaryLabel}>Personal best</span>
              <span className={styles.summaryVal}>{summaryStats.bestAcc}%</span>
            </div>
          </Tooltip>

          <Tooltip
            title={<span>Total <strong>number of questions</strong> you answered across all quizzes in this period</span>}
            {...CHIP_TOOLTIP}
          >
            <div className={styles.summaryChip}>
              <span className={styles.summaryDot} style={{ background: "#8b5cf6" }} />
              <span className={styles.summaryLabel}>Total answered</span>
              <span className={styles.summaryVal}>{summaryStats.totalQ} Q</span>
            </div>
          </Tooltip>

          <Tooltip
            title={<span>Total <strong>correct answers</strong> across all quizzes in this period</span>}
            {...CHIP_TOOLTIP}
          >
            <div className={styles.summaryChip}>
              <span className={styles.summaryDot} style={{ background: "#16a34a" }} />
              <span className={styles.summaryLabel}>Total correct</span>
              <span className={styles.summaryVal} style={{ color: "#16a34a" }}>{summaryStats.totalC}</span>
            </div>
          </Tooltip>
        </div>
      )}

      {/* ── Chart + legend ── */}
      <div className={styles.chartArea}>
      {/* Chart */}
      <div className={styles.chartBody}>
        {data.length > 0 ? (
          <DualAxes {...chartConfig} />
        ) : (
          <div className={styles.emptyChart}>No performance data yet</div>
        )}
      </div>

      {/* Custom legend panel */}
      <div className={styles.legendPanel}>
        <p className={styles.legendTitle}>Series</p>

        <button
          className={`${styles.legendChip} ${allOn ? styles.legendChipOn : styles.legendChipOff}`}
          onClick={toggleAll}
        >
          <span className={styles.legendLineIcon}>
            <span className={styles.legendLineSegment} style={{ borderColor: "#1a1a1a", borderStyle: "solid" }} />
          </span>
          <span className={styles.legendChipLabel}>All</span>
        </button>

        <div className={styles.legendDivider} />

        {SERIES.map((s) => {
          const on = active.has(s.key);
          return (
            <button
              key={s.key}
              className={`${styles.legendChip} ${on ? styles.legendChipOn : styles.legendChipOff}`}
              onClick={() => toggle(s.key)}
              title={on ? `Hide ${s.label}` : `Show ${s.label}`}
            >
              <span className={styles.legendLineIcon}>
                <span
                  className={styles.legendLineSegment}
                  style={{
                    borderColor: on ? s.color : "#cbd5e1",
                    borderStyle: s.solid ? "solid" : "dashed",
                  }}
                />
              </span>
              <span className={styles.legendChipLabel}>{s.label}</span>
              {s.axis === "right" && (
                <span className={styles.legendAxisTag}>%</span>
              )}
            </button>
          );
        })}

        <div className={styles.legendDivider} />
        <p className={styles.legendHint}>
          <span className={styles.legendAxisDot} style={{ background: "#64748b" }} /> left axis = questions
        </p>
        <p className={styles.legendHint}>
          <span className={styles.legendAxisDot} style={{ background: "#64748b" }} /> right axis = %
        </p>
      </div>
      </div>
    </div>
  );
};

const computeStreakStats = (accuracyTrend = []) => {
  if (!accuracyTrend.length) {
    return { current: 0, longest: 0, totalDays: 0, lastActive: null };
  }

  const uniqueDates = [
    ...new Set(
      accuracyTrend
        .map((item) => {
          const d = new Date(item.date);
          return isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
        })
        .filter(Boolean)
    ),
  ].sort();

  if (!uniqueDates.length) {
    return { current: 0, longest: 0, totalDays: 0, lastActive: null };
  }

  const totalDays = uniqueDates.length;
  const lastActive = uniqueDates[uniqueDates.length - 1];

  // Longest streak across all dates
  let longestStreak = 1;
  let tempStreak = 1;
  for (let i = 1; i < uniqueDates.length; i++) {
    const prev = new Date(uniqueDates[i - 1]);
    const curr = new Date(uniqueDates[i]);
    const diffDays = Math.round((curr - prev) / 86400000);
    if (diffDays === 1) {
      tempStreak++;
      if (tempStreak > longestStreak) longestStreak = tempStreak;
    } else {
      tempStreak = 1;
    }
  }

  // Current streak from most recent date backwards
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const mostRecent = new Date(lastActive);
  mostRecent.setHours(0, 0, 0, 0);
  const diffFromToday = Math.round((today - mostRecent) / 86400000);

  let currentStreak = 0;
  if (diffFromToday <= 1) {
    currentStreak = 1;
    for (let i = uniqueDates.length - 2; i >= 0; i--) {
      const curr = new Date(uniqueDates[i + 1]);
      const prev = new Date(uniqueDates[i]);
      const diffDays = Math.round((curr - prev) / 86400000);
      if (diffDays === 1) {
        currentStreak++;
      } else break;
    }
  }

  longestStreak = Math.max(longestStreak, currentStreak);

  return { current: currentStreak, longest: longestStreak, totalDays, lastActive };
};

const StreakPanelSkeleton = () => (
  <div className={styles.row}>
    <Card className={styles.streakCard}>
      <div className={styles.sectionHeader}>
        <Skeleton.Input active size="small" className={styles.skeletonTitle} />
      </div>
      <div className={styles.skeletonHero}>
        <Skeleton.Avatar active size={56} shape="square" className={styles.skeletonFireIcon} />
        <div>
          <Skeleton.Input active size="large" className={styles.skeletonStreakNum} />
        </div>
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className={styles.skeletonStatRow}>
          <Skeleton.Input active size="small" className={styles.skeletonStatLabel} />
          <Skeleton.Input active size="small" className={styles.skeletonStatVal} />
        </div>
      ))}
    </Card>
    <div className={styles.skeletonChartCard} style={{ flex: 1 }}>
      <Skeleton.Input active size="small" className={styles.skeletonChartTitle} />
      <Skeleton active title={false} paragraph={{ rows: 7 }} />
    </div>
  </div>
);

const DashboardStreakPanel = ({ accuracyTrend = [] }) => {
  const user   = useSelector((state) => state?.app?.user?.info);
  const userId = user?.user_code ?? user?._id;

  const [chartDateRange, setChartDateRange] = useState(null);

  // ── Streak stats use the parent-supplied accuracyTrend (all-time, unfiltered) ──
  const streak = useMemo(() => computeStreakStats(accuracyTrend), [accuracyTrend]);

  // ── Chart has its own API call with its own date filter ────────────────
  const chartQueryBody = useMemo(() => {
    const body = { userId };
    if (chartDateRange?.[0] && chartDateRange?.[1]) {
      body.startDate = chartDateRange[0].startOf("day").toISOString();
      body.endDate   = chartDateRange[1].endOf("day").toISOString();
    }
    return body;
  }, [userId, chartDateRange]);

  const {
    data: chartApiData,
    isLoading: chartLoading,
    isFetching: chartFetching,
  } = useGetDashboardV2PerformanceChartQuery(chartQueryBody, {
    skip: !userId || DASHBOARD_USE_MOCK_DATA,
    refetchOnMountOrArgChange: true,
  });

  const resolvedChartTrend = DASHBOARD_USE_MOCK_DATA
    ? mockChartData.accuracyTrend
    : (chartApiData?.accuracyTrend ?? []);

  const trendChartData = useMemo(
    () =>
      resolvedChartTrend.map((item) => ({
        date:      getTimeDateString(item.date, false, true) || item.date,
        accuracy:  parseFloat(item.accuracy)  ?? 0,
        correct:   item.correct   ?? null,
        incorrect: item.incorrect ?? null,
      })),
    [resolvedChartTrend]
  );

  const isFetching = chartFetching;
  const loading    = chartLoading && !resolvedChartTrend.length;

  const dateLabel = chartDateRange?.[0] && chartDateRange?.[1]
    ? `${chartDateRange[0].format("MMM D")} – ${chartDateRange[1].format("MMM D, YY")}`
    : "All time";

  if (loading) return <StreakPanelSkeleton />;

  const isActive = streak.current > 0;

  return (
    <div className={styles.row}>
      {/* ── Study Streak card — commented out, to be improved and re-added later ──
      <Card className={styles.streakCard}>
        <div className={styles.cardContentWrap}>
          <div className={styles.sectionHeader}>
            <Title level={5} className={styles.sectionTitle}>
              Study Streak
            </Title>
          </div>

          <div className={styles.streakHero}>
            <div className={styles.fireIconWrap}>
              <FireOutlined />
            </div>
            <div className={styles.streakHeroText}>
              <div className={`${styles.streakNumber} ${isActive ? styles.streakNumberActive : ""}`}>
                {streak.current}
              </div>
              <Text className={styles.streakLabel}>
                {isActive ? "day streak — keep it up!" : "No active streak"}
              </Text>
            </div>
          </div>

          <div className={styles.streakStats}>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>
                <span className={styles.statIcon} style={{ background: "#fffbeb", color: "#d97706" }}>
                  <TrophyOutlined />
                </span>
                Longest streak
              </span>
              <span className={styles.statValue}>{streak.longest} day{streak.longest !== 1 ? "s" : ""}</span>
            </div>

            <div className={styles.statRow}>
              <span className={styles.statLabel}>
                <span className={styles.statIcon} style={{ background: "#eff6ff", color: "#4b93ff" }}>
                  <CalendarOutlined />
                </span>
                Total study days
              </span>
              <span className={styles.statValue}>{streak.totalDays}</span>
            </div>

            <div className={styles.statRow}>
              <span className={styles.statLabel}>
                <span className={styles.statIcon} style={{ background: "#f0fdf4", color: "#16a34a" }}>
                  <ClockCircleOutlined />
                </span>
                Last active
              </span>
              <span className={styles.statValue}>
                {streak.lastActive
                  ? getTimeDateString(streak.lastActive, false, true) || streak.lastActive
                  : "—"}
              </span>
            </div>

            {!isActive && streak.totalDays > 0 && (
              <div className={styles.noActivityBadge}>
                <InfoCircleOutlined />
                Take a quiz today to start a streak!
              </div>
            )}

            {streak.totalDays === 0 && (
              <div className={styles.noActivityBadge}>
                <InfoCircleOutlined />
                No activity recorded yet
              </div>
            )}
          </div>

          {isFetching && <div className={styles.shimmerOverlay} />}
        </div>
      </Card>
      ── End Study Streak card ── */}

      {/* ── Overall performance trend chart ── */}
      <Card className={styles.trendCard}>
        <div className={styles.cardContentWrap}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleGroup}>
              <Title level={5} className={styles.sectionTitle}>
                Overall Performance Over Time
              </Title>
              <span className={styles.dateLabel}>{dateLabel}</span>
            </div>
            <div className={styles.filterGroup}>
              <Tooltip title="Filter chart data by date range" placement="topRight">
                <FilterOutlined className={styles.filterIcon} />
              </Tooltip>
              <RangePicker
                size="small"
                value={chartDateRange}
                ranges={CHART_DATE_PRESETS}
                format="MMM D, YY"
                allowClear
                onChange={setChartDateRange}
                placeholder={["From", "To"]}
              />
            </div>
          </div>
          <TrendChart data={trendChartData} />
          {isFetching && <div className={styles.shimmerOverlay} />}
        </div>
      </Card>
    </div>
  );
};

export default DashboardStreakPanel;
